import { Player } from '../domain/entities';
import { ATTRIBUTES } from '../domain/constants';

const pickRandom = (arr: string[]) => arr[Math.floor(Math.random() * arr.length)];

const openingPhrases: Record<string, string[]> = {
    // Single attributes
    handling: ["A safe pair of hands.", "Commands their box with real authority."],
    reflexes: ["Possesses lightning-fast reflexes.", "An exceptional shot-stopper."],
    tackling: ["A tenacious tackler who loves a challenge.", "Strikers hate playing against them."],
    marking: ["Sticks to their attacker like glue.", "Reads attacking movement superbly."],
    heading: ["Dominant in the air.", "A huge threat from set pieces at both ends."],
    positioning: ["Reads the game brilliantly.", "Always seems to be in the right place at the right time."],
    passing: ["The team's metronome.", "Can pick a pass from anywhere on the pitch."],
    dribbling: ["A tricky customer who can glide past defenders.", "Loves to run at the opposition."],
    creativity: ["The team's maestro.", "Can unlock any defence with a moment of magic."],
    finishing: ["A natural goalscorer.", "Give them half a chance, and the ball is in the net."],
    pace: ["Has blistering pace that terrifies defenders.", "A constant threat on the counter-attack."],
    // Attribute pairs
    'handling-reflexes': ["An all-round top quality goalkeeper.", "Combines sharp reflexes with reliable handling."],
    'tackling-marking': ["A classic, no-nonsense centre-back.", "An absolute rock at the back who loves to defend."],
    'passing-creativity': ["A gifted playmaker who dictates the tempo of the game.", "The creative hub of the team."],
    'finishing-pace': ["A deadly striker who combines lethal finishing with electric pace.", "The complete modern forward."],
    'dribbling-pace': ["An electrifying winger who can beat a defender with speed or skill.", "Almost impossible to stop in a one-on-one situation."],
};

const personalityPhrases: Record<string, string[]> = {
    Ambitious: ["...and you get the sense they're always looking to improve and reach the next level.", "...possesses a strong drive to be the best."],
    Loyal: ["...a real club player who gives their all for the badge.", "...the sort of player who leaves everything on the pitch for the team."],
    Professional: ["...a model professional who brings a great attitude to the training ground.", "...praised by coaches for their dedication and professionalism."],
    'Hot-headed': ["...but that fiery temperament can sometimes get the better of them.", "...plays with their heart on their sleeve, for better or worse."]
};

const fitnessPhrases: Record<string, string[]> = {
    highMax: ["They've got a fantastic engine and can run all day.", "Their stamina is a massive asset, especially late in games."],
    lowMax: ["Can struggle with the physical demands of a long season.", "Doesn't have the biggest tank and may need to be managed carefully."],
    highRecovery: ["Seems to bounce back from tough games incredibly quickly.", "Recovers from knocks and fatigue faster than most."],
    lowRecovery: ["Takes a while to get back to full sharpness after a match.", "Can be slow to recover, making them a rotation risk."]
};

const formPhrases: Record<string, string[]> = {
    positive: ["On top of that, they're currently enjoying a great spell of form.", "They're also in a rich vein of form at the moment."],
    negative: ["They seem to be going through a bit of a rough patch at the moment, however.", "Will be hoping to find their best form again soon, as they've been struggling lately."]
};

const ageClosingPhrases: Record<string, string[]> = {
    young: ["This kid has a very bright future ahead of them.", "Definitely one to watch for the future."],
    peak: ["Right in the prime of their career.", "At the peak of their powers right now."],
    experienced: ["Their experience is a real asset to the squad.", "Brings a calming presence and years of know-how."]
};


const getPunditTake = (player: Player): string => {
    const { position, skill, age, potential, attributes, form, morale, personality, fitnessMax, fitnessRecovery } = player;

    // --- High Priority Archetype Checks ---

    if (age < 21 && potential > 85) {
        return pickRandom([
            "This kid is a generational talent, a true wonderkid in the making. Their potential is off the charts and they're already making an impact. A future superstar.",
            "Scouts are raving about this youngster. With a potential rating that high at such a young age, they have all the tools to go to the very top of the game."
        ]);
    }
    if (age > 33 && skill > 75) {
        return pickRandom([
            `A seasoned veteran who has seen it all. The legs might not be what they were, but the football brain is as sharp as ever. Their experience is simply invaluable to the team.`,
            `A classic professional who leads by example on and off the pitch. Their quality and understanding of the game haven't faded one bit over the years.`
        ]);
    }
    if (form >= 2 && (morale === 'Ecstatic' || morale === 'Happy')) {
         return pickRandom([
            `This player is absolutely on fire at the moment. Everything they touch turns to gold, and they're playing with a smile on their face. A joy to watch.`,
            `In the form of their life right now. Confidence is sky-high, and it's showing in their performances. They look unstoppable.`
        ]);
    }
    if (form <= -2 && (morale === 'Furious' || morale === 'Unhappy')) {
         return pickRandom([
            `Stuck in a real crisis of confidence. Nothing seems to be going right for them on the pitch, and it's visibly affecting their game. Needs an arm around the shoulder.`,
            `You have to worry about this player's form. They look a shadow of their former self and are struggling to make any positive impact in matches.`
        ]);
    }

    // --- Build a Composite Take ---
    const parts: string[] = [];

    // Part 1: Opener based on top one or two attributes
    const keyAttrsForPos = ATTRIBUTES[position];
    const sortedAttrs = keyAttrsForPos
        .map(attr => ({ name: attr, value: attributes[attr] }))
        .sort((a, b) => b.value - a.value);

    const topAttr = sortedAttrs[0];
    const secondAttr = sortedAttrs[1];

    // Try to find a phrase for a pair of attributes first
    const pairKey1 = `${topAttr.name}-${secondAttr.name}`;
    const pairKey2 = `${secondAttr.name}-${topAttr.name}`;

    if (openingPhrases[pairKey1] && topAttr.value > skill + 5 && secondAttr.value > skill) {
        parts.push(pickRandom(openingPhrases[pairKey1]));
    } else if (openingPhrases[pairKey2] && topAttr.value > skill + 5 && secondAttr.value > skill) {
        parts.push(pickRandom(openingPhrases[pairKey2]));
    } else {
        // Fallback to single best attribute
        parts.push(pickRandom(openingPhrases[topAttr.name]));
    }

    // Part 2: Personality commentary
    if (personality && personalityPhrases[personality]) {
        parts.push(pickRandom(personalityPhrases[personality]));
    }

    // Part 3: Fitness commentary
    if (fitnessMax > 95) parts.push(pickRandom(fitnessPhrases.highMax));
    else if (fitnessMax < 85) parts.push(pickRandom(fitnessPhrases.lowMax));
    if (fitnessRecovery > 13) parts.push(pickRandom(fitnessPhrases.highRecovery));
    else if (fitnessRecovery < 9) parts.push(pickRandom(fitnessPhrases.lowRecovery));

    // Part 4: Form commentary
    if (form > 0) {
        parts.push(pickRandom(formPhrases.positive));
    } else if (form < 0) {
        parts.push(pickRandom(formPhrases.negative));
    }

    // Part 5: Age/Career stage closing
    if (age < 24) {
        parts.push(pickRandom(ageClosingPhrases.young));
    } else if (age < 30) {
        parts.push(pickRandom(ageClosingPhrases.peak));
    } else {
        parts.push(pickRandom(ageClosingPhrases.experienced));
    }

    return parts.join(' ');
};

const getManagerialActionText = (isPositive: boolean): string => {
    if (isPositive) {
        return pickRandom([
            "A tactical masterstroke from the manager changed the game's complexion.",
            "The manager's passionate touchline exhortations clearly inspired the players.",
            "A shrewd substitution proved to be the turning point."
        ]);
    } else {
        return pickRandom([
            "A questionable tactical decision seemed to unsettle the team.",
            "The manager looked unable to respond as the opposition took control.",
            "You have to question the timing of that substitution."
        ]);
    }
}

export const FlavorTextService = {
    getPunditTake,
    getManagerialActionText,
};